import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Save, Plus, Pencil, Trash2, ExternalLink, LayoutTemplate, BookMarked, ShieldCheck,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ContentAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { content, refresh } = useContent();

  return (
    <Layout showFooter={false}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Super Admin
            </span>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-forest-900">Content Studio</h1>
            <p className="mt-2 text-charcoal-600">Edit the public site content and manage policy resources. Signed in as {user?.name}.</p>
          </div>
          <Button variant="secondary" onClick={() => navigate("/")} className="rounded-full bg-bone-100 hover:bg-bone-200 text-forest-800 border border-forest-600/10">
            <ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> View site
          </Button>
        </div>

        <Tabs defaultValue="site" className="mt-8">
          <TabsList className="bg-bone-100 rounded-full p-1">
            <TabsTrigger value="site" data-testid="tab-site-content" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-forest-900 px-5">
              <LayoutTemplate className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Site Content
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-forest-900 px-5">
              <BookMarked className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Resources
            </TabsTrigger>
          </TabsList>

          <TabsContent value="site" className="mt-6">
            <SiteContentForm content={content} refresh={refresh} />
          </TabsContent>
          <TabsContent value="resources" className="mt-6">
            <ResourceManager refresh={refresh} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm">
      <h2 className="font-serif text-lg text-forest-900 mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-forest-900 font-semibold text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "bg-bone rounded-lg border-forest-600/15 focus-visible:ring-terracotta-400/50";

function SiteContentForm({ content, refresh }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      hero: { ...content.hero },
      mission: { ...content.mission, bullets: (content.mission.bullets || []).join("\n") },
      contact: { ...content.contact },
      campuses: (content.campuses || []).join("\n"),
    });
  }, [content]);

  if (!form) return null;
  const setHero = (k, v) => setForm((f) => ({ ...f, hero: { ...f.hero, [k]: v } }));
  const setMission = (k, v) => setForm((f) => ({ ...f, mission: { ...f.mission, [k]: v } }));
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero: form.hero,
        mission: {
          eyebrow: form.mission.eyebrow,
          title: form.mission.title,
          body: form.mission.body,
          bullets: form.mission.bullets.split("\n").map((s) => s.trim()).filter(Boolean),
        },
        contact: form.contact,
        campuses: form.campuses.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/content", payload);
      await refresh();
      toast.success("Site content saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <Section title="Hero section">
        <Field label="Eyebrow badge"><Input data-testid="content-hero-badge" value={form.hero.badge} onChange={(e) => setHero("badge", e.target.value)} className={inputCls} /></Field>
        <Field label="Headline"><Input data-testid="content-hero-title" value={form.hero.title} onChange={(e) => setHero("title", e.target.value)} className={inputCls} /></Field>
        <Field label="Subtitle"><Textarea data-testid="content-hero-subtitle" rows={3} value={form.hero.subtitle} onChange={(e) => setHero("subtitle", e.target.value)} className={`${inputCls} resize-none`} /></Field>
      </Section>

      <Section title="Mission section">
        <Field label="Eyebrow"><Input data-testid="content-mission-eyebrow" value={form.mission.eyebrow} onChange={(e) => setMission("eyebrow", e.target.value)} className={inputCls} /></Field>
        <Field label="Title"><Input data-testid="content-mission-title" value={form.mission.title} onChange={(e) => setMission("title", e.target.value)} className={inputCls} /></Field>
        <Field label="Body"><Textarea data-testid="content-mission-body" rows={4} value={form.mission.body} onChange={(e) => setMission("body", e.target.value)} className={`${inputCls} resize-none`} /></Field>
        <Field label="Highlights (one per line)"><Textarea data-testid="content-mission-bullets" rows={4} value={form.mission.bullets} onChange={(e) => setMission("bullets", e.target.value)} className={`${inputCls} resize-none`} /></Field>
      </Section>

      <Section title="Contact & campuses">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Contact email"><Input data-testid="content-contact-email" value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} className={inputCls} /></Field>
          <Field label="Contact phone"><Input data-testid="content-contact-phone" value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Campuses (one per line — used in intake & registration)"><Textarea data-testid="content-campuses" rows={6} value={form.campuses} onChange={(e) => setForm((f) => ({ ...f, campuses: e.target.value }))} className={`${inputCls} resize-none`} /></Field>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="content-save-btn" className="rounded-full bg-forest-600 hover:bg-forest-700 text-bone px-7">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" strokeWidth={1.5} /> Save site content</>}
        </Button>
      </div>
    </motion.div>
  );
}

const EMPTY_POLICY = { slug: "", title: "", category: "Resource", summary: "", content: "", order: 99 };

function ResourceManager({ refresh }) {
  const [policies, setPolicies] = useState(null);
  const [editing, setEditing] = useState(null); // policy object or EMPTY for new
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/policies").then((r) => setPolicies(r.data)).catch(() => setPolicies([]));
  useEffect(() => { load(); }, []);

  const openEdit = async (slug) => {
    try {
      const { data } = await api.get(`/policies/${slug}`);
      setEditing(data); setIsNew(false);
    } catch { toast.error("Could not load resource."); }
  };
  const openNew = () => { setEditing({ ...EMPTY_POLICY }); setIsNew(true); };

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      if (isNew) await api.post("/policies", editing);
      else await api.put(`/policies/${editing.slug}`, editing);
      setEditing(null);
      await load();
      await refresh();
      toast.success(isNew ? "Resource created." : "Resource updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save resource.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/policies/${deleteTarget.slug}`);
      setDeleteTarget(null);
      await load();
      await refresh();
      toast.success("Resource deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete.");
    }
  };

  const setE = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex justify-between items-center">
        <p className="text-sm text-charcoal-600">Policies and guides shown in the public Resources library.</p>
        <Button onClick={openNew} data-testid="resource-new-btn" className="rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} /> New resource
        </Button>
      </div>

      <div className="mt-5 grid gap-3">
        {policies === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div>
        ) : policies.length === 0 ? (
          <p className="text-charcoal-600 py-8 text-center">No resources yet.</p>
        ) : (
          policies.map((p) => (
            <div key={p.slug} data-testid={`resource-row-${p.slug}`} className="flex items-center justify-between gap-4 bg-white rounded-2xl border border-forest-600/10 p-5 shadow-sm">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-charcoal-600 bg-bone-100 rounded-full px-2.5 py-0.5">{p.category}</span>
                  <span className="text-xs text-charcoal-600">#{p.order}</span>
                </div>
                <h3 className="mt-1.5 font-serif text-xl text-forest-900 truncate">{p.title}</h3>
                <p className="text-sm text-charcoal-600 truncate">{p.summary}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(p.slug)} data-testid={`resource-edit-${p.slug}`} className="rounded-full text-forest-700 hover:bg-forest-50"><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`resource-delete-${p.slug}`} className="rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit / create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-bone rounded-2xl max-h-[88vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl text-forest-900 text-left">{isNew ? "New resource" : "Edit resource"}</DialogTitle>
                <DialogDescription className="text-left">Fill in the details below. This resource appears in the public Resources library.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Title"><Input data-testid="resource-title-input" value={editing.title} onChange={(e) => setE("title", e.target.value)} className={inputCls} /></Field>
                  <Field label="Category"><Input data-testid="resource-category-input" value={editing.category} onChange={(e) => setE("category", e.target.value)} className={inputCls} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Summary"><Input data-testid="resource-summary-input" value={editing.summary} onChange={(e) => setE("summary", e.target.value)} className={inputCls} /></Field>
                  <Field label="Order"><Input type="number" data-testid="resource-order-input" value={editing.order} onChange={(e) => setE("order", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
                </div>
                <Field label="Content (use ## for headings, - for bullets)">
                  <Textarea data-testid="resource-content-input" rows={12} value={editing.content} onChange={(e) => setE("content", e.target.value)} className={`${inputCls} resize-none font-mono text-sm`} />
                </Field>
                {isNew && <p className="text-xs text-charcoal-600">The URL slug is generated from the title automatically.</p>}
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setEditing(null)} className="rounded-full bg-bone-100 hover:bg-bone-200 text-forest-800 border border-forest-600/10">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="resource-save-btn" className="rounded-full bg-forest-600 hover:bg-forest-700 text-bone">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-bone rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-forest-900">Delete this resource?</AlertDialogTitle>
            <AlertDialogDescription>“{deleteTarget?.title}” will be permanently removed from the public library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="resource-confirm-delete" className="rounded-full bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
